/**
 * Utility functions to wait for page media (especially videos) to be fully loaded and autoplaying
 */

/**
 * Wait for all videos on the current page to be loaded and playing
 * @param timeoutMs Maximum time to wait (default: 10000ms = 10 seconds)
 * @returns Promise that resolves when all videos are ready, or after timeout
 */
export async function waitForPageVideos(timeoutMs: number = 10000): Promise<void> {
  return new Promise((resolve) => {
    const startTime = Date.now();
    
    const checkVideos = () => {
      const videos = Array.from(document.querySelectorAll('video')) as HTMLVideoElement[];
      
      if (videos.length === 0) {
        // No videos on page, resolve immediately
        resolve();
        return;
      }

      // Check if all videos are ready and playing
      const allReady = videos.every((video) => {
        // Video is ready if:
        // 1. It has enough data to play (readyState >= 2 = HAVE_CURRENT_DATA)
        // 2. It's playing OR it's paused but ready (autoplay might have been blocked)
        const hasData = video.readyState >= 2; // HAVE_CURRENT_DATA or better
        const isPlaying = !video.paused && !video.ended && video.currentTime > 0;
        const canPlay = hasData && (isPlaying || video.paused); // Paused but ready means autoplay blocked, but it's loaded
        
        return canPlay;
      });

      const elapsed = Date.now() - startTime;
      
      if (allReady) {
        // All videos are ready
        resolve();
      } else if (elapsed >= timeoutMs) {
        // Timeout reached, resolve anyway (don't block forever)
        if (process.env.NODE_ENV === 'development') {
          console.warn('[waitForPageVideos] Timeout reached, some videos may not be ready');
        }
        resolve();
      } else {
        // Check again after a short delay
        setTimeout(checkVideos, 200);
      }
    };

    // Wait a bit for videos to start loading
    setTimeout(checkVideos, 500);
    
    // Also set a timeout to prevent infinite waiting
    setTimeout(() => {
      resolve();
    }, timeoutMs);
  });
}

/**
 * Wait for all images on the current page to be loaded
 * @param timeoutMs Maximum time to wait (default: 5000ms = 5 seconds)
 * @returns Promise that resolves when all images are loaded, or after timeout
 */
export async function waitForPageImages(timeoutMs: number = 5000): Promise<void> {
  return new Promise((resolve) => {
    const startTime = Date.now();
    let checkCount = 0;
    
    const checkImages = () => {
      const images = Array.from(document.querySelectorAll('img')) as HTMLImageElement[];
      
      if (images.length === 0) {
        resolve();
        return;
      }

      // Prioritize checking fallback images and hero section images first
      const heroSections = document.querySelectorAll('section[id="hero"], section:first-of-type');
      const heroImages: HTMLImageElement[] = [];
      heroSections.forEach(section => {
        const sectionImages = Array.from(section.querySelectorAll('img')) as HTMLImageElement[];
        heroImages.push(...sectionImages);
      });

      // Check if all images are loaded
      // For hero images, be more strict (they must be complete)
      // For other images, allow some leniency
      const allLoaded = images.every((img) => {
        // Data URLs and blob URLs are considered loaded
        if (img.src.startsWith('data:') || img.src.startsWith('blob:')) {
          return true;
        }
        
        // Check if image is complete and has dimensions
        const isComplete = img.complete && img.naturalWidth > 0;
        
        // For hero images, require complete loading
        const isHeroImage = heroImages.includes(img);
        if (isHeroImage) {
          return isComplete;
        }
        
        // For other images, allow some leniency (might still be loading in background)
        return isComplete;
      });

      const elapsed = Date.now() - startTime;
      
      if (allLoaded) {
        resolve();
      } else if (elapsed >= timeoutMs) {
        // Timeout reached
        if (process.env.NODE_ENV === 'development') {
          console.warn('[waitForPageImages] Timeout reached, some images may not be loaded');
        }
        resolve();
      } else {
        // Check more frequently initially (30ms for first 10 checks, then 50ms, then 100ms)
        let checkInterval = 200;
        if (checkCount < 10) {
          checkInterval = 30;
        } else if (checkCount < 20) {
          checkInterval = 50;
        } else if (checkCount < 30) {
          checkInterval = 100;
        }
        checkCount++;
        setTimeout(checkImages, checkInterval);
      }
    };

    // Start checking immediately (no delay for faster detection)
    setTimeout(checkImages, 0);
    
    // Set a timeout as backup
    setTimeout(() => {
      resolve();
    }, timeoutMs);
  });
}

/**
 * Wait for all media (videos and images) on the current page to be loaded
 * Videos must be ready/playing, images must be loaded
 * @param timeoutMs Maximum time to wait (default: 10000ms for videos)
 * @returns Promise that resolves when all media is ready
 */
export async function waitForPageMedia(timeoutMs: number = 10000): Promise<void> {
  // Wait for both videos and images in parallel
  await Promise.all([
    waitForPageVideos(timeoutMs),
    waitForPageImages(Math.min(timeoutMs, 5000)), // Images timeout faster
  ]);
}

